import { Badge } from "@/components/ui/badge";
import { ArrowDownLeft, ArrowUpRight, Sparkles } from "lucide-react";
import type {
  HubspotPartnerDealRelationship,
  PortalDealRow,
} from "@colab/shared";

// Small chip indicating whether a deal is inbound (referred to Bookboost by
// the partner), outbound-introduced (Bookboost referred the customer to the
// partner), or outbound-influenced (co-driven).
//
// Inbound vs outbound is the bucket's signature constraint (plan quality
// bar) — the badge is the primary touch point for that distinction on the
// list page. Keep it visually distinct from the status badge.

type Variant = "default" | "secondary" | "outline";

function classifyLabel(
  direction: "inbound" | "outbound",
  relationship: HubspotPartnerDealRelationship | null,
): { label: string; variant: Variant; icon: React.ComponentType<{ className?: string }> } {
  if (direction === "inbound") {
    return { label: "Inbound", variant: "default", icon: ArrowDownLeft };
  }
  if (relationship === "referred_to_them") {
    return { label: "Introduced", variant: "secondary", icon: ArrowUpRight };
  }
  if (relationship === "influenced") {
    return { label: "Co-influenced", variant: "secondary", icon: Sparkles };
  }
  // Pre-HubSpot partner-submitted outbound (deal_type=co_sell/reseller) —
  // generic outbound label.
  return { label: "Outbound", variant: "secondary", icon: ArrowUpRight };
}

export function AttributionDirectionBadge({ row }: { row: PortalDealRow }) {
  const { label, variant, icon: Icon } = classifyLabel(
    row.direction,
    row.relationship,
  );
  return (
    <Badge variant={variant} className="gap-1">
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </Badge>
  );
}
